/*
 * Admin assistant — transcript model, saved-chat API, document attachments and HTML export
 * (NEXUS-1384 chat v2).
 */

import { getFn, postFn } from './apiClient';
import type { ChatTurn, ChatUsage } from './assistantChat';
import { reportDigest, type ReportResult } from './reports';
import { escHtml, markdownToHtml } from './markdown';
import { formatCell, linkFor, type Row } from '../components/DataGrid';

// ── Transcript ──────────────────────────────────────────────────────────

export interface AttachmentMeta {
  name: string;
  size: number;
  chars: number;
  truncated: boolean;
}

export interface Attachment extends AttachmentMeta {
  id: string;
  text: string;
}

export type UiMessage =
  | { kind: 'user'; id: string; text: string; at: string; by?: string | null; attachments?: AttachmentMeta[] }
  | {
      kind: 'assistant';
      id: string;
      text: string;
      at: string;
      pending?: boolean;
      waitMs?: number | null;
      genMs?: number | null;
      totalMs?: number | null;
      usage?: ChatUsage | null;
      model?: string | null;
      /** The AI service that answered ("Nexus AI (ai.kstack.app)", "Anthropic"…). Shown to platform admins only — forViewer strips it for client staff. */
      provider?: string | null;
      /** A clarifying question's answers — buttons that send the chosen one as the next message. */
      choices?: string[];
    }
  | { kind: 'report'; id: string; result: ReportResult; at: string; ms?: number; args?: Record<string, unknown> }
  | { kind: 'report-loading'; id: string; title: string; at: string }
  | {
      kind: 'action';
      id: string;
      at: string;
      action: 'create_issue';
      title: string;
      description: string;
      severity?: string;
      status: 'proposed' | 'creating' | 'created' | 'cancelled' | 'error';
      issueId?: string;
      issueNumber?: number | null;
      error?: string;
      by?: string | null;
    }
  | {
      kind: 'error';
      id: string;
      text: string;
      at: string;
      /** An AI-service failure: raw text is kept for platform admins; staff see a neutral line. */
      ai?: boolean;
      status?: number;
    };

export const TOOL_RESULT_NUDGE =
  'The chart and grid are on screen. Answer my question in 1-3 short sentences.';

/** The model-side history for a transcript (used when a saved chat is continued). */
export function turnsFromMessages(ms: UiMessage[]): ChatTurn[] {
  const out: ChatTurn[] = [];
  for (const m of ms) {
    if (m.kind === 'user') {
      const notes = (m.attachments ?? []).map((a) => `(attached earlier: ${a.name} — contents not kept in history)`);
      out.push({ role: 'user', content: [m.text, ...notes].filter(Boolean).join('\n') });
    } else if (m.kind === 'assistant' && m.text) {
      out.push({ role: 'assistant', content: m.text });
    } else if (m.kind === 'action') {
      out.push({
        role: 'user',
        content: `\`\`\`tool_result\n${JSON.stringify({ tool: 'create_issue', status: m.status, issueNumber: m.issueNumber ?? null })}\n\`\`\``,
      });
    } else if (m.kind === 'report') {
      out.push({
        role: 'user',
        content: `\`\`\`tool_result\n${JSON.stringify({ ok: true, ...reportDigest(m.result) })}\n\`\`\`\n${TOOL_RESULT_NUDGE}`,
      });
    }
  }
  // Keep it alternating-friendly: merge consecutive same-role turns.
  const merged: ChatTurn[] = [];
  for (const t of out) {
    const last = merged[merged.length - 1];
    if (last && last.role === t.role) last.content += `\n\n${t.content}`;
    else merged.push({ ...t });
  }
  return merged;
}

/** Messages as saved: no pending rows, report rows capped so a transcript stays small. */
export function forSave(ms: UiMessage[]): UiMessage[] {
  return ms
    .filter((m) => m.kind !== 'report-loading' && !(m.kind === 'assistant' && m.pending))
    .map((m) =>
      m.kind === 'report' && m.result.rows.length > 200
        ? { ...m, result: { ...m.result, rows: m.result.rows.slice(0, 200), subtitle: `${m.result.subtitle ?? ''} (first 200 rows saved)` } }
        : m,
    );
}

export function titleFrom(ms: UiMessage[]): string {
  const first = ms.find((m) => m.kind === 'user') as Extract<UiMessage, { kind: 'user' }> | undefined;
  const t = (first?.text || first?.attachments?.[0]?.name || 'New chat').replace(/\s+/g, ' ').trim();
  return t.length > 80 ? `${t.slice(0, 79)}…` : t;
}

// ── Saved chats API ─────────────────────────────────────────────────────

export interface ChatSummary {
  id: string;
  title: string | null;
  page: string | null;
  userId: string;
  userName: string | null;
  userRole: string | null;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
  mine: boolean;
}

export interface SavedChat extends Omit<ChatSummary, 'messageCount'> {
  messages: UiMessage[];
}

export function listChats(opts: { q?: string; scope?: 'mine' | 'all' } = {}) {
  return getFn<{ chats: ChatSummary[]; canSeeAll: boolean }>('aquastarAssistantChats', {
    q: opts.q || undefined,
    scope: opts.scope,
    limit: 60,
  });
}

/** Drop anything in a saved transcript the console can't render (defensive: rows are client-written). */
export function sanitizeMessages(raw: unknown): UiMessage[] {
  if (!Array.isArray(raw)) return [];
  const out: UiMessage[] = [];
  for (const m of raw as Array<Record<string, unknown>>) {
    if (!m || typeof m !== 'object' || typeof m.id !== 'string' || typeof m.at !== 'string') continue;
    if ((m.kind === 'user' || m.kind === 'assistant' || m.kind === 'error') && typeof m.text === 'string') {
      const choices = Array.isArray(m.choices)
        ? (m.choices as unknown[]).filter((c): c is string => typeof c === 'string' && c.length > 0).map((c) => c.slice(0, 200)).slice(0, 6)
        : undefined;
      out.push({ ...(m as object), pending: undefined, choices: choices?.length ? choices : undefined } as UiMessage);
    } else if (m.kind === 'action' && m.action === 'create_issue' && typeof m.title === 'string') {
      const st = m.status === 'creating' ? 'proposed' : m.status;
      out.push({ ...(m as object), status: st } as UiMessage);
    } else if (m.kind === 'report') {
      const r = m.result as Record<string, unknown> | undefined;
      if (r && Array.isArray(r.columns) && Array.isArray(r.rows) && typeof r.title === 'string') out.push(m as unknown as UiMessage);
    }
  }
  return out;
}

export async function loadChat(id: string): Promise<SavedChat> {
  const r = await getFn<{ chat: SavedChat }>('aquastarAssistantChats', { id });
  return { ...r.chat, messages: sanitizeMessages(r.chat.messages) };
}

export function saveChat(input: { id?: string | null; title: string; page: string; messages: UiMessage[] }) {
  return postFn<{ id: string; updatedAt: string }>('aquastarAssistantChatSave', {
    id: input.id || undefined,
    title: input.title,
    page: input.page,
    messages: forSave(input.messages),
  });
}

export function deleteChat(id: string) {
  return postFn<{ deleted: boolean }>('aquastarAssistantChatDelete', { id });
}

// ── Attachments ─────────────────────────────────────────────────────────

export const MAX_FILE_BYTES = 400_000;
export const MAX_ATTACH_CHARS = 60_000; // per message, across all attachments
const TEXT_EXT = /\.(txt|md|markdown|csv|tsv|json|log|xml|html?|ya?ml|sql|ini|conf|cfg|js|ts|py|sh|rtf)$/i;

export function isTextFile(f: File): boolean {
  return f.type.startsWith('text/') || /json|xml|csv|yaml|javascript|sql/.test(f.type) || TEXT_EXT.test(f.name);
}

let aseq = 0;
export const attachmentId = () => `a${Date.now().toString(36)}${(aseq++).toString(36)}`;

export async function readAttachment(f: File): Promise<Attachment> {
  if (!isTextFile(f)) {
    throw new Error(
      `${f.name}: only text documents can be attached for now (txt, md, csv, json, log …). Images and PDFs aren't supported by the assistant yet.`,
    );
  }
  if (f.size > MAX_FILE_BYTES) throw new Error(`${f.name} is ${(f.size / 1024).toFixed(0)} KB — the limit is ${MAX_FILE_BYTES / 1000} KB.`);
  const text =
    typeof f.text === 'function'
      ? await f.text()
      : await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result ?? ''));
          r.onerror = () => reject(new Error(`Could not read ${f.name}`));
          r.readAsText(f);
        });
  return { id: attachmentId(), name: f.name, size: f.size, chars: text.length, truncated: false, text };
}

export function pastedAttachment(text: string, n: number): Attachment {
  return { id: attachmentId(), name: `Pasted text ${n}.txt`, size: text.length, chars: text.length, truncated: false, text };
}

/** The user turn as the model sees it: prompt + attachment blocks (shared budget). */
export function composeUserContent(prompt: string, atts: Attachment[]): { content: string; metas: AttachmentMeta[] } {
  let budget = MAX_ATTACH_CHARS;
  const metas: AttachmentMeta[] = [];
  const blocks: string[] = [];
  for (const a of atts) {
    const take = Math.max(0, Math.min(a.text.length, budget));
    budget -= take;
    const truncated = take < a.text.length;
    metas.push({ name: a.name, size: a.size, chars: a.chars, truncated });
    if (take > 0) blocks.push(`\`\`\`attachment name="${a.name.replace(/"/g, "'")}"${truncated ? ' truncated="true"' : ''}\n${a.text.slice(0, take)}\n\`\`\``);
  }
  return { content: [prompt, ...blocks].filter(Boolean).join('\n\n'), metas };
}

// ── Formatting ──────────────────────────────────────────────────────────

export function fmtMs(ms: number | null | undefined): string | null {
  if (ms === null || ms === undefined) return null;
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`;
}

export function statsLine(m: Extract<UiMessage, { kind: 'assistant' }>): string[] {
  const parts: string[] = [];
  // "Nexus AI (ai.kstack.app) · qwen3-vl:30b" — which service and which model answered. provider is
  // only present on messages prepared for a platform admin (forViewer).
  if (m.provider && m.model) parts.push(`${m.provider} · ${m.model}`);
  else if (m.provider || m.model) parts.push((m.provider || m.model) as string);
  const wait = fmtMs(m.waitMs);
  const gen = fmtMs(m.genMs);
  if (wait) parts.push(`first token ${wait}`);
  if (gen && (m.genMs ?? 0) > 0) parts.push(`generated in ${gen}`);
  const out = m.usage?.completionTokens;
  if (out) {
    const rate = m.genMs && m.genMs > 200 ? ` (${Math.round(out / (m.genMs / 1000))}/s)` : '';
    const inTok = m.usage?.promptTokens ? ` · ${m.usage.promptTokens.toLocaleString()} in` : '';
    parts.push(`${out.toLocaleString()} tokens out${rate}${inTok}`);
  }
  return parts;
}

export const fmtClock = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

// ── HTML export ─────────────────────────────────────────────────────────

function reportHtml(r: ReportResult, svg: string | null, origin: string): string {
  const cols = r.columns;
  const head = cols.map((c) => `<th class="${c.type === 'number' ? 'num' : ''}">${escHtml(c.label)}</th>`).join('');
  const body = (r.rows as Row[])
    .map(
      (row) =>
        `<tr>${cols
          .map((c) => {
            const t = escHtml(formatCell(c, row[c.key]));
            const href = linkFor(c, row);
            return `<td class="${c.type === 'number' ? 'num' : ''}">${href ? `<a href="${escHtml(origin + href)}">${t}</a>` : t}</td>`;
          })
          .join('')}</tr>`,
    )
    .join('');
  return `<section class="report"><div class="rt">${escHtml(r.title)}</div>${r.subtitle ? `<div class="rs">${escHtml(r.subtitle)}</div>` : ''}${
    svg ? `<div class="chart">${svg}</div>` : ''
  }<div class="tw"><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div></section>`;
}

/** What client staff see for an AI-service failure — never server text (it can name services). */
export function neutralAiError(status?: number): string {
  if (status === 429) return 'The assistant is busy right now. Please try again in a moment.';
  if (status === 401 || status === 403) return 'The assistant is not available for your account.';
  return 'The assistant could not answer just now. Please try again in a moment.';
}

/**
 * Messages as this viewer may see them. Saved chats keep the raw details (which AI service
 * answered, its error text) so platform admins can diagnose; everyone else gets the model only
 * and a neutral line for AI-service failures — in the chat, in history and in exports.
 */
export function forViewer(ms: UiMessage[], isPlatformAdmin: boolean): UiMessage[] {
  if (isPlatformAdmin) return ms;
  return ms.map((m) => {
    if (m.kind === 'assistant' && m.provider) return { ...m, provider: null };
    if (m.kind === 'error' && m.ai) return { ...m, text: neutralAiError(m.status) };
    return m;
  });
}

export function buildChatHtml(input: {
  title: string;
  messages: UiMessage[];
  owner: string | null;
  exportedBy: string | null;
  createdAt?: string | null;
  chartSvgs: Record<string, string | null>;
  origin: string;
}): string {
  const items = input.messages
    .filter((m) => m.kind !== 'report-loading')
    .map((m) => {
      const when = escHtml(new Date(m.at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }));
      if (m.kind === 'user') {
        const att = (m.attachments ?? [])
          .map((a) => `<span class="chip">📄 ${escHtml(a.name)} · ${escHtml((a.size / 1024).toFixed(1))} KB${a.truncated ? ' (cut)' : ''}</span>`)
          .join('');
        return `<div class="u"><div class="ub">${escHtml(m.text).replace(/\n/g, '<br>')}${att ? `<div class="atts">${att}</div>` : ''}</div><div class="meta r">${m.by ? `${escHtml(m.by)} · ` : ''}${when}</div></div>`;
      }
      if (m.kind === 'assistant') {
        const stats = statsLine(m).map(escHtml).join(' · ');
        return `<div class="a">${markdownToHtml(m.text || '')}<div class="meta">Assistant · ${when}${stats ? ` · ${stats}` : ''}</div></div>`;
      }
      if (m.kind === 'action') {
        const st =
          m.status === 'created'
            ? `Created issue${m.issueNumber ? ` #${escHtml(m.issueNumber)}` : ''}`
            : m.status === 'cancelled'
              ? 'Cancelled'
              : m.status === 'error'
                ? `Failed: ${escHtml(m.error ?? '')}`
                : 'Proposed — not created';
        return `<div class="a"><section class="report"><div class="rt">Issue: ${escHtml(m.title)}</div><div class="rs">${st}</div>${markdownToHtml(m.description || '')}</section><div class="meta">Action · ${when}</div></div>`;
      }
      if (m.kind === 'report') {
        return `<div class="a">${reportHtml(m.result, input.chartSvgs[m.id] ?? null, input.origin)}<div class="meta">Report · ${when}${m.ms ? ` · ran in ${escHtml(fmtMs(m.ms))}` : ''}</div></div>`;
      }
      return `<div class="err">${escHtml(m.text)}</div>`;
    })
    .join('\n');
  const sub = [
    input.owner ? `Conversation by ${escHtml(input.owner)}` : null,
    input.createdAt ? `started ${escHtml(new Date(input.createdAt).toLocaleString())}` : null,
    `exported ${escHtml(new Date().toLocaleString())}${input.exportedBy ? ` by ${escHtml(input.exportedBy)}` : ''}`,
  ]
    .filter(Boolean)
    .join(' · ');
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escHtml(input.title)}</title>
<style>
:root{color-scheme:light}
body{font:15px/1.55 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#1d2433;background:#f5f7fb;margin:0}
main{max-width:860px;margin:0 auto;padding:28px 18px 60px}
h1{font-size:21px;margin:0 0 4px}.sub{color:#647087;font-size:13px;margin-bottom:22px}
.u{display:flex;flex-direction:column;align-items:flex-end;margin:16px 0}
.ub{background:#019ddc;color:#fff;border-radius:16px;padding:9px 14px;max-width:85%;white-space:normal}
.atts{margin-top:6px}.chip{display:inline-block;background:rgba(255,255,255,.2);border-radius:8px;padding:2px 8px;font-size:12px;margin:2px 4px 0 0}
.a{margin:16px 0}.a p{margin:0 0 8px}
.meta{color:#8a93a3;font-size:12px;margin-top:4px}.meta.r{text-align:right}
.err{background:#fff4e5;border-radius:8px;padding:8px 12px;margin:12px 0;font-size:13px}
.report{background:#fff;border-radius:12px;box-shadow:0 1px 3px rgba(16,28,66,.08);padding:14px 16px}
.rt{font-weight:700}.rs{color:#647087;font-size:12px;margin-bottom:6px}
.chart{overflow-x:auto;margin:6px 0}.chart svg{max-width:100%;height:auto}
.tw{overflow-x:auto}table{border-collapse:collapse;width:100%;font-size:13px}
th,td{padding:6px 9px;border-bottom:1px solid #e6e9ef;text-align:left;white-space:nowrap}th{background:#f4f6f9}
.num{text-align:right;font-variant-numeric:tabular-nums}
pre{background:#eef1f6;border-radius:8px;padding:10px;white-space:pre-wrap}code{background:#eef1f6;border-radius:4px;padding:0 4px}
a{color:#0177b5}
@media print{body{background:#fff}.report{box-shadow:none;border:1px solid #e6e9ef}}
</style></head>
<body><main>
<h1>${escHtml(input.title)}</h1>
<div class="sub">${sub}</div>
${items}
</main></body></html>`;
}
